import React from "react";
import { ArrowLeft, ArrowRight, Bell, ExternalLink, History, Layers3, Map as MapIcon, Radio, Sigma } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import CaseStudyCta from "../components/layout/CaseStudyCta";
import { MapMock, TimelineMock, PulseMock } from "../components/solutions/EmergencyMocks";
import "../styles/may17.css";
import "../styles/emergency-preparedness.css";

// Solution: emergency preparedness for municipalities. The map, timeline and
// roaming board are illustrations built from the app's styling with made-up
// figures (components/solutions/EmergencyMocks.jsx), not screenshots. The demo
// further down is the real app with made-up data (telcofy-demo
// scripts/build-public-demo.sh); swap DEMO_URL to the subdomain once connected.
const DEMO_URL = "https://telcofy-beredskap.web.app";
const DEMO_START = `${DEMO_URL}/realtime/project?project=demo-sentrum`;

const features = [
  { icon: MapIcon, title: "Your own zones", body: "Draw or upload the areas you care about, from a square to a procession route, and switch monitoring on when you need it." },
  { icon: Radio, title: "Updated every 10 minutes", body: "Estimated people present in every zone, with colours showing which areas are rising, steady or falling." },
  { icon: Layers3, title: "International presence", body: "Switch to visitors on foreign mobile networks, see their change over 10, 20 and 30 minutes, and which countries they come from." },
  { icon: Sigma, title: "Saved totals", body: "Combine zones, such as a city centre or an event route, into one figure each on the map." },
  { icon: Bell, title: "Above the usual", body: "A night-time baseline shows the activity above each zone's normal level, so the event stands out from the people who are always there." },
  { icon: History, title: "Look back", body: "A dated timeline shows earlier hours and days, so you can compare the event with a normal day." },
];

const EmergencyPreparednessPage = () => (
  <div className="may17-page">
    <Navbar />
    <main className="pt-16">
      <header className="may17-hero ep-hero">
        <Link to="/solutions" className="may17-back">
          <ArrowLeft size={15} aria-hidden="true" /> Solutions
        </Link>
        <div className="may17-hero-inner">
          <div className="may17-hero-copy ep-hero-copy">
            <p className="may17-kicker">Solution · Municipalities</p>
            <h1>
              See where people gather, <span>as it happens</span>
            </h1>
            <p className="may17-deck">
              A live map for emergency preparedness teams: estimated people present in each of your zones, updated every 10 minutes,
              from anonymised, aggregated mobile network data. No individual is tracked.
            </p>
            <ul className="may17-meta">
              <li><MapIcon size={15} aria-hidden="true" />Your own zones</li>
              <li><Radio size={15} aria-hidden="true" />Updated every 10 minutes</li>
              <li><Layers3 size={15} aria-hidden="true" />People present and international presence</li>
            </ul>
          </div>
        </div>
      </header>

      <section className="ep-shot-band">
        <div className="may17-wrap ep-mock-hero">
          <MapMock />
        </div>
      </section>

      <section className="may17-decisions may17-wrap">
        <div className="may17-decisions-head">
          <h2>What you get</h2>
          <p className="ep-lead">
            Built for the days a city is used differently: national days, festivals, large events and incidents. Each municipality sets up
            its own zones and totals, and decides when monitoring runs.
          </p>
        </div>
        <div className="ep-features">
          {features.map(({ icon: Icon, title, body }) => (
            <article key={title}>
              <Icon aria-hidden="true" />
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="may17-wrap ep-mock-pair">
        <TimelineMock />
        <PulseMock />
      </section>

      <section className="may17-wrap ep-related">
        <p className="ep-related-label">Related project</p>
        <Link to="/projects/may-17th" className="ep-related-card">
          <div>
            <h3>17 May in Oslo</h3>
            <p>How central Oslo fills and empties on Constitution Day, zone by zone, through the day.</p>
          </div>
          <ArrowRight aria-hidden="true" />
        </Link>
      </section>


      <section className="may17-picture">
        <div className="may17-wrap">
          <h2 className="ep-h2">Try the app</h2>
          <p className="ep-lead">
            The real app, running on made-up figures for central Bergen. Open a zone, switch to international presence, try the
            roaming pulse and the country breakdown, or draw your own area.
          </p>
          <div className="ep-frame">
            <iframe src={DEMO_START} title="The Telcofy app with made-up figures for central Bergen" loading="lazy" />
          </div>
          <a className="ep-open" href={DEMO_START} target="_blank" rel="noreferrer">
            Open the app in a new tab <ExternalLink size={14} aria-hidden="true" />
          </a>
        </div>
      </section>

      <CaseStudyCta
        question="Leading emergency preparedness in a large Norwegian municipality?"
        body="Book a conversation to see the map, talk through your own zones and saved totals, and tell us what reporting you would need."
        extra={
          <p className="text-ink-muted">
            Planning a big public day? See{" "}
            <Link to="/projects/may-17th" className="font-semibold text-brand-700 underline underline-offset-2">
              17 May in Oslo
            </Link>
            .
          </p>
        }
      />

      <footer className="may17-method">
        <strong>About the data</strong>
        <p>
          Estimates from anonymised, aggregated mobile network data, scaled up from one operator's subscribers to the whole population.
          No individual is tracked. International means devices on a non-Norwegian mobile network. The demo's town, zones and figures
          are invented.
        </p>
      </footer>
    </main>
  </div>
);

export default EmergencyPreparednessPage;
